import { Tooltip as ReactTooltip } from "react-tooltip";
import { normalizeResolutions } from "../../helper/common";

const VerdictPanel = ({
  _t = (key, fallback) => fallback || key,
  individualWarnings = {},
  individualResolutions = {},
  selectedResolveTenantIds = [],
  toggleResolveTenant,
  formatMissingDoc,
  expandedDocs = {},
  forceExpandDocs = false,
  toggleDocsExpanded,
  hasPendingChanges,
  resolvingDocs,
  resolveActionLabel,
  onResolveClick,
  blockingRows = [],
  guarantorItems = [],
  kycWarnings = {},
  onKycTenantClick,
  isVerdictLoading = false,
}) => {
  const warnings =
    individualWarnings && typeof individualWarnings === "object"
      ? individualWarnings
      : {};
  const selectedIds = Array.isArray(selectedResolveTenantIds)
    ? selectedResolveTenantIds
    : [];
  const expandedByTenant =
    expandedDocs && typeof expandedDocs === "object" ? expandedDocs : {};
  const safeBlockingRows = Array.isArray(blockingRows) ? blockingRows : [];
  const safeGuarantorItems = Array.isArray(guarantorItems)
    ? guarantorItems
    : [];
  const hasMissing = Object.keys(warnings).length > 0;
  const kycWarningsSafe =
    kycWarnings && typeof kycWarnings === "object" ? kycWarnings : {};
  const hasKyc = Object.keys(kycWarningsSafe).length > 0;
  const hasToResolve = hasMissing || hasKyc;
  const hasBlocking = safeBlockingRows.length > 0;
  const hasGuarantor = safeGuarantorItems.length > 0;

  if (isVerdictLoading) {
    return (
      <>
        <div className="linked-report__divider" />
        <div
          className="verdict-panel__group verdict-panel__group--loading"
          aria-busy="true"
          aria-live="polite"
          style={{ minHeight: 96 }}
        >
          <h3 className="verdict-panel__heading">
            {_t("toResolve", "TO RESOLVE")}
          </h3>
          <p
            className="verdict-panel__subtitle verdict-panel__subtitle--loading"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 0,
            }}
          >
            <span className="verdict-panel__spinner" aria-hidden="true" />
            {_t(
              "checkingVerifications",
              "Checking document and verification status…"
            )}
          </p>
        </div>
      </>
    );
  }

  if (!hasMissing && !hasKyc && !hasBlocking && !hasGuarantor) return null;

  return (
    <>
      <div className="linked-report__divider" />
      {hasGuarantor && (
        <div className="verdict-panel__group">
          <h3 className="verdict-panel__heading">
            {_t("guarantorRecommended", "Guarantor recommended")}
          </h3>
          <ul className="verdict-panel__list" style={{ marginTop: 6 }}>
            {safeGuarantorItems.map((item, index) => (
              <li
                key={`guarantor-${item.id || item.tenantName || index}`}
                className="verdict-panel__row"
              >
                <span className="verdict-panel__lock" aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <div className="verdict-panel__row-content">
                  <div className="verdict-panel__row-title">
                    {item.tenantName}
                  </div>
                  <div
                    className="verdict-panel__row-subtitle"
                    style={{ display: "block" }}
                  >
                    {item.reason}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasToResolve && (
        <div className="verdict-panel__group">
          <div className="verdict-panel__group-header">
            <div>
              <h3 className="verdict-panel__heading">
                {_t("toResolve", "TO RESOLVE")}
              </h3>
              <p className="verdict-panel__subtitle">
                {hasMissing
                  ? _t(
                      "toResolveSubtitle",
                      "Ask tenants for the missing documents, or provide a reason to continue despite the alert."
                    )
                  : _t(
                      "kycToResolveSubtitle",
                      "Some KYC/AML checks require review — click a tenant to open their verification details."
                    )}
              </p>
            </div>
            {hasPendingChanges && (
              <div
                className="report-overview__resolve-actions no-print"
                style={{ margin: 0 }}
              >
                <button
                  type="button"
                  className="report-overview__resolve-action"
                  onClick={onResolveClick}
                  disabled={resolvingDocs}
                >
                  <span>
                    {resolvingDocs
                      ? `${_t("updating", "Updating")}...`
                      : resolveActionLabel}
                  </span>
                </button>
              </div>
            )}
          </div>
          <ul className="verdict-panel__list">
            {Object.entries(warnings).map(([tenantId, warning]) => {
              const isResolved = !!warning?.isResolved;
              const resolution = normalizeResolutions(
                individualResolutions?.[tenantId]
              )?.[0];
              const isChecked = tenantId && selectedIds.includes(tenantId);
              const docs = warning?.docsMissing || [];
              const isExpanded =
                forceExpandDocs || !!expandedByTenant[tenantId];
              const visibleDocs = isExpanded ? docs : docs.slice(0, 3);
              const remaining = docs.length - 3;
              return (
                <li key={tenantId} className="verdict-panel__row">
                  <label
                    className="custom-checkbox no-print"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  >
                    <input
                      type="checkbox"
                      checked={!!isChecked}
                      disabled={!tenantId}
                      onChange={() => toggleResolveTenant(tenantId)}
                    />
                    <span className="custom-checkmark" />
                  </label>
                  <div className="verdict-panel__row-content">
                    <div
                      className="verdict-panel__row-title"
                      style={
                        isResolved
                          ? { textDecoration: "line-through", color: "#A3A3A3" }
                          : undefined
                      }
                    >
                      {warning?.label} {_t("has", "has")} {docs.length}{" "}
                      {_t("missingDocuments", "missing documents")}
                    </div>
                    {docs.length > 0 && (
                      <div
                        className={`verdict-panel__row-subtitle ${isExpanded ? "verdict-panel__row-subtitle--expanded" : ""}`}
                        style={
                          isResolved
                            ? {
                                textDecoration: "line-through",
                                color: "#A3A3A3",
                              }
                            : undefined
                        }
                      >
                        ({visibleDocs.map(formatMissingDoc).join(", ")}
                        {!isExpanded && docs.length > 3 ? ", ...)" : ")"}
                        {!forceExpandDocs && !isExpanded && remaining > 0 && (
                          <button
                            type="button"
                            className="verdict-panel__expand"
                            onClick={() => toggleDocsExpanded(tenantId)}
                          >
                            +{remaining} {_t("more", "more")}
                          </button>
                        )}
                        {!forceExpandDocs && isExpanded && docs.length > 3 && (
                          <button
                            type="button"
                            className="verdict-panel__expand"
                            onClick={() => toggleDocsExpanded(tenantId)}
                          >
                            {_t("showLess", "Show less")}
                          </button>
                        )}
                      </div>
                    )}
                    {isResolved && resolution?.landlordName && (
                      <span className="report-overview__resolved-by">
                        {_t("resolvedByLandlord", "Resolved by")}{" "}
                        <strong>{resolution?.landlordName}</strong>
                      </span>
                    )}
                    {isResolved && resolution?.note && (
                      <>
                        <span
                          className="report-overview__resolved-note no-print"
                          data-tooltip-id={`verdict-note-${tenantId}`}
                        >
                          i
                        </span>
                        <ReactTooltip
                          id={`verdict-note-${tenantId}`}
                          place="top"
                          noArrow
                          className="react-tooltip-styling report-overview-resolved-tooltip"
                          render={() => <p>{resolution?.note}</p>}
                        />
                        <span
                          className="pdf-only"
                          style={{
                            color: "#6f7e7e",
                            fontStyle: "italic",
                            fontWeight: 400,
                          }}
                        >
                          — {resolution?.note}
                        </span>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
            {Object.entries(kycWarningsSafe).map(([tenantId, warning]) => {
              const unresolvedCount =
                warning?.unresolvedCount ?? warning?.unresolved?.length ?? 0;
              const categoryLabel =
                unresolvedCount === 1
                  ? _t("categoryNeedsReview", "category needs review")
                  : _t("categoriesNeedReview", "categories need review");
              return (
                <li
                  key={`kyc-${tenantId}`}
                  className="verdict-panel__row"
                  role={onKycTenantClick ? "button" : undefined}
                  tabIndex={onKycTenantClick ? 0 : undefined}
                  onClick={() => onKycTenantClick && onKycTenantClick(tenantId)}
                  onKeyDown={(e) => {
                    if (
                      onKycTenantClick &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      onKycTenantClick(tenantId);
                    }
                  }}
                  style={{
                    cursor: onKycTenantClick ? "pointer" : undefined,
                  }}
                >
                  <span
                    className="verdict-panel__lock"
                    aria-hidden="true"
                    style={{ marginTop: 2, flexShrink: 0 }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4" />
                      <path d="M12 16h.01" />
                    </svg>
                  </span>
                  <div className="verdict-panel__row-content">
                    <div
                      className="verdict-panel__row-title"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span>
                        {warning?.label}{" "}
                        {_t("kycRequiresReview", "KYC/AML requires review")}
                      </span>
                      <img
                        src="/images/external-link.svg"
                        alt=""
                        className="no-print"
                        style={{
                          width: 14,
                          height: 14,
                          flexShrink: 0,
                        }}
                      />
                    </div>
                    <div
                      className="verdict-panel__row-subtitle"
                      style={{ display: "block" }}
                    >
                      {unresolvedCount > 0
                        ? `${unresolvedCount} ${categoryLabel} — ${_t(
                            "clickToViewKyc",
                            "click to view details"
                          )}`
                        : _t(
                            "kycReviewNeeded",
                            "KYC review needed — click to view"
                          )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {hasBlocking && (
        <div className="verdict-panel__group">
          <h3 className="verdict-panel__heading">
            {_t("blocking", "BLOCKING")}
          </h3>
          <p className="verdict-panel__subtitle">
            {_t(
              "blockingSubtitle",
              "These issues cannot be resolved by the landlord and prevent this application from being approved."
            )}
          </p>
          <ul className="verdict-panel__list">
            {safeBlockingRows.map((row, index) => (
              <li
                key={row.key || `${row.title}-${index}`}
                className="verdict-panel__row"
              >
                <span className="verdict-panel__lock" aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    <circle cx="12" cy="16" r="1" />
                  </svg>
                </span>
                <div className="verdict-panel__row-content">
                  <div className="verdict-panel__row-title">{row.title}</div>
                  {row.desc && (
                    <div
                      className="verdict-panel__row-subtitle"
                      style={{ display: "block", WebkitLineClamp: "unset" }}
                    >
                      {row.desc}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default VerdictPanel;
